import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Target, Globe, TrendingUp, Recycle, Battery, Sun, Cpu, CheckCircle2 } from 'lucide-react';
import { sustainabilityStats, futureInitiatives } from '../data/mock';

const iconMap = { Battery, Sun, Cpu };

const SustainabilityPage = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 lg:pt-12 min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3735218/pexels-photo-3735218.jpeg"
            alt="Sustainability"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#0a1628]/90 to-[#0a1628]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-4">
                Sustainability
              </p>
              <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
                Building for <span className="font-semibold">Net Zero 2050</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                We're not just managing waste — we're engineering the infrastructure for a circular economy, aligned with the UAE's Net Zero 2050 Strategy.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-8 py-4 font-semibold transition-colors">
                Partner With Us <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20">
                  <div className="text-5xl font-bold text-[#4fd1c5] mb-2">98%</div>
                  <div className="text-white/80">Recovery Rate</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20">
                  <div className="text-5xl font-bold text-white mb-2">Zero</div>
                  <div className="text-white/80">Landfill Policy</div>
                </div>
                <div className="bg-[#4fd1c5] p-8">
                  <div className="text-5xl font-bold text-[#0a1628] mb-2">50K</div>
                  <div className="text-[#0a1628]/80">MT Capacity/Year</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-8 border border-white/20">
                  <div className="text-5xl font-bold text-white mb-2">GCC</div>
                  <div className="text-white/80">Regional Impact</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                The E-Waste Challenge
              </p>
              <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-6">
                A Growing <span className="font-semibold">Global Crisis</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Electronic waste is the fastest-growing waste stream globally. Less than 1% is recycled formally and responsibly. Grenew is working to change this reality in the GCC region.
              </p>
              
              <div className="bg-[#0a1628] p-8 mb-8">
                <div className="flex items-start gap-4">
                  <TrendingUp className="w-8 h-8 text-[#4fd1c5] flex-shrink-0" />
                  <div>
                    <div className="text-2xl font-bold text-white mb-2">62+ Million Tonnes</div>
                    <div className="text-gray-400">of e-waste generated globally each year, growing by 3-5% annually</div>
                  </div>
                </div>
              </div>
              
              <Link to="/contact" className="inline-flex items-center gap-2 text-[#4fd1c5] font-semibold hover:text-[#38b2ac] transition-colors">
                Learn How We Can Help <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {sustainabilityStats.map((stat, i) => (
                <div key={i} className={`p-6 ${i === 0 ? 'bg-[#4fd1c5]' : 'bg-[#f8fafc] border border-gray-200'}`}>
                  <div className={`text-3xl lg:text-4xl font-bold mb-2 ${i === 0 ? 'text-[#0a1628]' : 'text-[#0a1628]'}`}>
                    {stat.stat}
                  </div>
                  <div className={`text-sm mb-1 ${i === 0 ? 'text-[#0a1628]/80' : 'text-gray-600'}`}>
                    {stat.label}
                  </div>
                  <div className={`text-xs font-semibold ${i === 0 ? 'text-[#0a1628]/60' : 'text-[#4fd1c5]'}`}>
                    {stat.growth}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-20 lg:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
              Our Impact
            </p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-4">
              Enabling <span className="font-semibold">Sustainable Transformation</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              We help reduce landfill dependency, conserve resources, and create green jobs across the region.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              { 
                icon: Leaf, 
                title: 'Reduce Landfill Dependency', 
                desc: 'Diverting thousands of tons of e-waste from landfills annually through proper recycling and recovery processes.',
                stat: '50K MT',
                statLabel: 'Annual capacity'
              },
              { 
                icon: Target, 
                title: 'Conserve Resources', 
                desc: 'Recovering valuable materials like gold, silver, copper, and rare earth elements, reducing the need for mining.',
                stat: '98%',
                statLabel: 'Recovery rate'
              },
              { 
                icon: Globe, 
                title: 'Create Green Jobs', 
                desc: 'Supporting the growth of sustainable industries and creating employment opportunities in the circular economy.',
                stat: '500+',
                statLabel: 'Jobs enabled'
              },
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 border border-gray-200 hover:border-[#4fd1c5] transition-colors group">
                <div className="w-16 h-16 bg-[#0a1628] flex items-center justify-center mb-6 group-hover:bg-[#4fd1c5] transition-colors">
                  <item.icon className="w-8 h-8 text-[#4fd1c5] group-hover:text-[#0a1628] transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-[#0a1628] mb-3">{item.title}</h3>
                <p className="text-gray-500 mb-6">{item.desc}</p>
                <div className="pt-6 border-t border-gray-100">
                  <div className="text-2xl font-bold text-[#4fd1c5]">{item.stat}</div>
                  <div className="text-sm text-gray-400">{item.statLabel}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Roadmap */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://images.pexels.com/photos/459411/pexels-photo-459411.jpeg"
                alt="Future"
                className="w-full aspect-[4/3] object-cover grayscale"
              />
              <div className="absolute inset-0 bg-[#0a1628]/60 flex items-center justify-center">
                <div className="text-center text-white">
                  <Recycle className="w-20 h-20 mx-auto mb-4 text-[#4fd1c5]" />
                  <span className="text-3xl font-semibold">Net Zero 2050</span>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <p className="text-[#4fd1c5] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
                Future Roadmap
              </p>
              <h2 className="text-3xl lg:text-4xl font-light text-[#0a1628] mb-6">
                Expanding <span className="font-semibold">Capabilities</span>
              </h2>
              <p className="text-gray-600 mb-8">
                As technology evolves, so do we. We're preparing for emerging waste streams that will define the next decade.
              </p>
              
              <div className="space-y-6">
                {futureInitiatives.map((init, i) => {
                  const IconComponent = iconMap[init.icon] || Battery;
                  return (
                    <div key={i} className="flex items-start gap-4 p-6 bg-[#f8fafc] border border-gray-200">
                      <div className="w-12 h-12 bg-[#4fd1c5]/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-[#4fd1c5]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0a1628] mb-1">{init.title}</h4>
                        <p className="text-gray-500 text-sm">{init.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#0a1628]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(79, 209, 197, 0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-light text-white mb-6">
            Join the <span className="font-semibold">Movement</span>
          </h2>
          <p className="text-xl text-gray-400 mb-10">
            Partner with us in building a sustainable future for the region. Together, we can close the loop on electronic waste.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#4fd1c5] hover:bg-[#38b2ac] text-[#0a1628] px-10 py-5 font-semibold text-lg transition-colors">
              Request a Pickup <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact#partner" className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-10 py-5 font-semibold text-lg transition-colors">
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SustainabilityPage;
